import type { PermissionGatedTab } from '@scripsteam/scrips-signal-ds';

/**
 * Account Settings tab model (PROD-1200, epic PROD-1199).
 *
 * Tab order + visibility mirror Flutter `doctor_base_widget.dart:129-253`:
 *   - not-boarded → Personal + Account ONLY
 *   - boarded     → + Professional / Educational / Schedule / Practices
 *   - Billing     → only with the `Billing_Settings` permission (DATA gate)
 *   - Macro       → only when `shouldShowAddMacro`
 *   - Order sets  → straight after Macro, only for a physician on their OWN settings
 *                   (PROD-2710; no Flutter counterpart — see `canSeeOrderSetsTab`)
 *
 * `BUILT_TABS` is the subset shipped so far — a tab is only rendered once its
 * slice lands (PROD-1201..1205), so the strip never shows a dead-end tab. Add
 * the tab id here when its slice ships. The visibility rules below already
 * encode the full intended set, so growth is one line.
 */
export type SettingsTabId =
  | 'personal'
  | 'account'
  | 'professional'
  | 'educational'
  | 'schedule'
  | 'practices'
  | 'billing'
  | 'macro'
  | 'orderSets';

export interface SettingsTabSpec {
  id: SettingsTabId;
  label: string;
  /** UPPERCASE breadcrumb leaf (Flutter `Header` breadcrumb). */
  breadcrumbLabel: string;
}

export const ALL_SETTINGS_TABS: readonly SettingsTabSpec[] = [
  { id: 'personal', label: 'Personal Details', breadcrumbLabel: 'PERSONAL DETAILS' },
  { id: 'account', label: 'Account Details', breadcrumbLabel: 'ACCOUNT DETAILS' },
  { id: 'professional', label: 'Professional Details', breadcrumbLabel: 'PROFESSIONAL DETAILS' },
  { id: 'educational', label: 'Educational Details', breadcrumbLabel: 'EDUCATIONAL DETAILS' },
  { id: 'schedule', label: 'Schedule', breadcrumbLabel: 'SCHEDULE' },
  { id: 'practices', label: 'Practices', breadcrumbLabel: 'PRACTICES' },
  { id: 'billing', label: 'Billing', breadcrumbLabel: 'BILLING' },
  { id: 'macro', label: 'Macro', breadcrumbLabel: 'MACRO' },
  { id: 'orderSets', label: 'Order sets', breadcrumbLabel: 'ORDER SETS' },
];

/** Tabs whose content slice has shipped. Personal + Account land in PROD-1200. */
export const BUILT_TABS: ReadonlySet<SettingsTabId> = new Set<SettingsTabId>([
  'personal',
  'account',
  'professional',
  'educational',
  'schedule',
  'practices',
  'billing',
  'macro',
  'orderSets',
]);

export interface ComputeSettingsTabsInput {
  isNotBoarded: boolean;
  canSeeBilling: boolean;
  canSeeMacro: boolean;
  /** PROD-2710 — see `canSeeOrderSetsTab`. Absent → hidden. */
  canSeeOrderSets?: boolean;
  /** Override the shipped set (tests / Storybook). Defaults to `BUILT_TABS`. */
  builtTabs?: ReadonlySet<SettingsTabId>;
}

/**
 * Resolve the in-scope tab specs given boarding + permission DATA. A tab is
 * included when (a) the boarding/permission rule passes AND (b) its slice has
 * shipped (`builtTabs`). Returns spec objects in canonical order.
 */
export function resolveSettingsTabSpecs({
  isNotBoarded,
  canSeeBilling,
  canSeeMacro,
  canSeeOrderSets = false,
  builtTabs = BUILT_TABS,
}: ComputeSettingsTabsInput): SettingsTabSpec[] {
  return ALL_SETTINGS_TABS.filter((t) => {
    if (!builtTabs.has(t.id)) return false;
    if (isNotBoarded) return t.id === 'personal' || t.id === 'account';
    if (t.id === 'billing') return canSeeBilling;
    if (t.id === 'macro') return canSeeMacro;
    if (t.id === 'orderSets') return canSeeOrderSets;
    return true;
  });
}

/**
 * Order sets tab gate (PROD-2710, release 1: personal sets).
 *
 * - PHYSICIAN ONLY: `mayOrder` (the `ORDERING_PERSONA_IDS` allowlist in `auth/roles.ts`,
 *   PROD-2669), read from `useRole().mayOrder`. The same gate the A/P order verbs use; the
 *   server's 403 is the authority.
 * - OWN SETTINGS ONLY (`accountMode`): the OrderSets API has no provider id and always answers
 *   with the CALLER's sets, so an admin viewing another practitioner's page would be shown their
 *   own sets under that practitioner's name.
 */
export function canSeeOrderSetsTab({
  accountMode,
  mayOrder,
}: {
  accountMode: boolean;
  mayOrder: boolean;
}): boolean {
  return accountMode && mayOrder;
}

/**
 * Map the resolved specs to DS `PermissionGatedTab[]`. Every in-scope tab is
 * `visible` — permission gating happens upstream by INCLUSION (a tab the user
 * may not see is simply absent), matching Flutter's build-time tab list. The
 * `locked` state is reserved for future cross-module reuse.
 */
export function computeSettingsTabs(
  input: ComputeSettingsTabsInput,
): PermissionGatedTab[] {
  return resolveSettingsTabSpecs(input).map((t) => ({
    id: t.id,
    label: t.label,
    permission: 'visible' as const,
  }));
}

// ===========================================================================
// Nurse / staff self-edit tab model (PROD-1206).
//
// Tab order + visibility mirror Flutter `staff_base_widget.dart:107-156`:
//   - nurse     → Personal · Account · Professional · Schedule · Practices · Macro
//   - non-nurse → Personal · Account · Practices ONLY
//
// In PA the Account Settings route only ever dispatches NURSES to this page
// (doctors go to AccountSettingsPage), so the nurse 6-tab set is the live
// case. The non-nurse 3-tab degrade exists so a non-nurse staff landing here
// (shouldn't, but defends the route) gets a coherent surface rather than the
// nurse-only tabs. `isNurse` is the real data-driven role flag (src/auth/roles.ts),
// NOT a hardcoded matrix.
// ===========================================================================

/** Staff self-edit tab ids — a subset of `SettingsTabId`. */
export type NurseSettingsTabId = Extract<
  SettingsTabId,
  'personal' | 'account' | 'professional' | 'schedule' | 'practices' | 'macro'
>;

const NURSE_TABS: readonly NurseSettingsTabId[] = [
  'personal',
  'account',
  'professional',
  'schedule',
  'practices',
  'macro',
];

const NON_NURSE_STAFF_TABS: readonly NurseSettingsTabId[] = [
  'personal',
  'account',
  'practices',
];

export interface ResolveNurseSettingsTabsInput {
  isNurse: boolean;
}

/**
 * Resolve the in-scope staff self-edit tab specs. Nurse → full 6-tab set;
 * non-nurse staff → the 3-tab degrade. Returns spec objects in canonical
 * order, reusing the `ALL_SETTINGS_TABS` specs (shared label/breadcrumb).
 */
export function resolveNurseSettingsTabSpecs({
  isNurse,
}: ResolveNurseSettingsTabsInput): SettingsTabSpec[] {
  const allowed = new Set<SettingsTabId>(
    isNurse ? NURSE_TABS : NON_NURSE_STAFF_TABS,
  );
  return ALL_SETTINGS_TABS.filter((t) => allowed.has(t.id));
}

/** Map the resolved nurse specs to DS `PermissionGatedTab[]`. */
export function resolveNurseSettingsTabs(
  input: ResolveNurseSettingsTabsInput,
): PermissionGatedTab[] {
  return resolveNurseSettingsTabSpecs(input).map((t) => ({
    id: t.id,
    label: t.label,
    permission: 'visible' as const,
  }));
}
